import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LicenseBadge from "./LicenseBadge";
import MembershipCodes from "./MembershipCodes";
import { KeyRound, Trash2 } from "lucide-react";

export default function LicensesView({ groups, reload }) {
  const [form, setForm] = useState({ chatId: "", days: 30, clanTag: "" });
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full bg-obsidian border border-white/10 rounded-md text-white px-3 py-2.5 text-sm focus:border-gold focus:ring-1 focus:ring-gold outline-none";

  const activate = async (e) => {
    e.preventDefault();
    if (!form.chatId.trim() || !form.clanTag.trim()) {
      toast.error("Completa el ID del grupo y el tag del clan");
      return;
    }
    setBusy(true);
    try {
      await api.activate({ chatId: form.chatId.trim(), days: Number(form.days), clanTag: form.clanTag.trim() });
      toast.success("Licencia activada");
      setForm({ chatId: "", days: 30, clanTag: "" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al activar");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (chatId) => {
    try {
      await api.deactivate(chatId);
      toast.success("Plan desactivado");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <span className="overline text-gold">Gestión de planes</span>
        <h2 className="text-2xl md:text-3xl font-heading font-bold">Licencias</h2>
      </div>

      <div className="card-surface p-6" data-testid="activate-form-card">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="text-gold" size={18} />
          <h3 className="font-heading font-semibold">Activar plan en un grupo</h3>
        </div>
        <form onSubmit={activate} className="grid md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="overline block mb-1.5">ID del grupo (chatId)</label>
            <input data-testid="activate-chatid" className={inputCls} placeholder="1203630...@g.us" value={form.chatId} onChange={(e) => setForm({ ...form, chatId: e.target.value })} />
          </div>
          <div>
            <label className="overline block mb-1.5">Tag del clan</label>
            <input data-testid="activate-clantag" className={`${inputCls} font-mono`} placeholder="#2ABC123" value={form.clanTag} onChange={(e) => setForm({ ...form, clanTag: e.target.value })} />
          </div>
          <div>
            <label className="overline block mb-1.5">Días</label>
            <input data-testid="activate-days" type="number" min="1" max="3650" className={inputCls} value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} />
          </div>
          <div className="md:col-span-4">
            <button data-testid="activate-submit" disabled={busy} className="bg-gold text-black font-bold rounded-md px-6 py-2.5 text-sm hover:brightness-95 transition-all disabled:opacity-60">
              {busy ? "Activando…" : "Activar plan"}
            </button>
          </div>
        </form>
        <p className="text-xs text-muted-foreground mt-3">
          El ID del grupo aparece automáticamente en la pestaña Grupos cuando el bot recibe un comando.
          También puedes activarlo con el comando de admin <code className="text-gold">!activate &lt;días&gt; #TAG</code> dentro del grupo.
        </p>
      </div>

      <MembershipCodes groups={groups} />

      <div className="card-surface overflow-hidden" data-testid="licenses-table">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <span className="overline">Licencias registradas ({groups.length})</span>
        </div>
        {groups.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">No hay licencias todavía.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {groups.map((g) => {
              const ls = g.licenseState || {};
              return (
                <div key={g.chatId} className="flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-white/[0.02]" data-testid={`license-row-${g.chatId}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{g.name || "Grupo"}{g.clanName ? ` · 🏰 ${g.clanName}` : ""}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{g.clanTag || "—"} · {g.chatId}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-muted-foreground hidden sm:block" title={ls.remainingText || ""}>
                      {ls.active ? (ls.remainingText || `${ls.daysLeft}d`) + " restantes" : ls.status === "expired" ? "expirada" : "—"}
                    </span>
                    <LicenseBadge state={ls.status} />
                    {ls.active && (
                      <button data-testid={`license-deactivate-${g.chatId}`} onClick={() => deactivate(g.chatId)} className="text-crimson hover:bg-crimson/10 rounded-md p-1.5 transition-all">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
